import type { Comic, ReaderState, UploadKind, UploadStatus } from '@tanko/shared'

export interface SourceFileSnapshot { name: string; size: number; lastModified: number }
export interface ReceivedPart { page: number; kind: UploadKind }
/** 待傳的分片：每頁一個全尺寸圖，封面整本只有一張，記的是來源頁碼。 */
export interface PendingParts { pages: number[]; cover: number | null }
export interface PublicationSnapshot {
  /** 發布的目標目錄項。發布是狀態轉換，因此必須指名要把哪一筆轉為已備份。 */
  comicId: string
  title: string
  author: string
  coverPage: number
  /**
   * 沒有分類。發布是狀態轉換，不碰分類指派——作品在加入書庫時就註冊，分類在書庫指派；
   * 這次改動之前凍結的 snapshot 可能仍殘留 categoryIds，發布請求以具名欄位組裝以略過它。
   */
  progress: ({ page: number; offset: number; updatedAt: number; mutationId: string }) | null
  bookmarks: { id: string; page: number; offset: number; label: string; createdAt: number; updatedAt: number; mutationId: string }[]
}

export function sameSourceManifest(left: readonly SourceFileSnapshot[], right: readonly SourceFileSnapshot[]): boolean {
  return left.length === right.length && left.every((file, index) => {
    const other = right[index]
    return file.name === other.name && file.size === other.size && file.lastModified === other.lastModified
  })
}
/**
 * 每頁一個全尺寸圖，缺 receipt 或被驗證判為失敗的才重傳。封面只有一張：
 * 只有在沒有「頁碼等於目前封面頁」的 cover receipt 時才列入——別頁的舊封面不算數。
 * 驗證失敗只影響全尺寸圖，封面在發布時另外驗。
 */
export function pendingUploadParts(pageCount: number, coverPage: number, received: readonly ReceivedPart[], failedPage: number | null): PendingParts {
  const complete = new Set(received.map(part => `${part.page}:${part.kind}`))
  const pages: number[] = []
  for (let page = 0; page < pageCount; page += 1) {
    if (failedPage === page || !complete.has(`${page}:image`)) pages.push(page)
  }
  return { pages, cover: complete.has(`${coverPage}:cover`) ? null : coverPage }
}
/**
 * 準備上傳時的待辦分級。
 *
 * 分級的用途只有一個：決定要不要碰檔案系統。圖片早就傳完、只差一個不需要任何位元組的
 * 發布呼叫的作品，不該被來源資料夾授權與整本重讀這兩道無關的關卡擋住——那正是既有流程
 * 讓一本已驗證的 209 頁作品永遠完成不了的原因。
 */
export type UploadReadiness =
  /** session 已發布，只需把雲端識別碼寫回本機。 */
  | { tier: 'bound'; cloudComicId: string }
  /** 分片齊全且封面頁碼與即將發布的一致，只需送發布請求。 */
  | { tier: 'publish-only' }
  /** 其餘情況：要授權來源、讀取來源並補傳 pending 裡的分片。 */
  | { tier: 'needs-source'; pending: PendingParts }

export interface UploadSessionFacts {
  status: UploadStatus
  comicId: string | null
  received: readonly ReceivedPart[]
  lastErrorPage: number | null
}

/**
 * 依伺服器的 session 狀態判定這一本還剩什麼要做。
 *
 * `coverPage` 必須是「即將發布的那一頁」——已固定的發布快照優先，否則取 IndexedDB 當下的
 * 作品紀錄。用書庫 store 記憶體裡的值會在剛換過封面時與發布階段不一致，結果是伺服器以
 * 「缺少這一頁的封面縮圖」拒絕發布。
 */
export function uploadReadiness(session: UploadSessionFacts, pageCount: number, coverPage: number): UploadReadiness {
  if (session.status === 'ready' && session.comicId) return { tier: 'bound', cloudComicId: session.comicId }
  const pending = pendingUploadParts(pageCount, coverPage, session.received, session.lastErrorPage)
  if (session.status === 'verified' && !pending.pages.length && pending.cover === null) return { tier: 'publish-only' }
  return { tier: 'needs-source', pending }
}

/**
 * 來源快照比對的結果。
 *
 * 舊行為是一偵測到不符就取消伺服器端的 session。檔案被搬動、複製或從備份還原都會讓
 * 每一筆 lastModified 改變，於是已經傳完的兩百多張圖被作廢，使用者只能從頭再傳一次。
 * 「快照不符」其實只代表我們無法確定內容是否相同，不代表已上傳的分片失效，因此這裡
 * 只回報差異，不帶任何動作——續傳與重新上傳兩條路都留給使用者選。
 *
 * 不由客戶端主動取消也沒有殘留問題：伺服器端既有的七天 TTL 與每日清理排程會處理。
 */
export interface SourceComparison {
  changed: boolean
  /** 不符時給使用者看的說明，兩條出口都在訊息裡明講；相符時為空字串。 */
  message: string
}

export function compareUploadSource(
  recorded: readonly SourceFileSnapshot[], current: readonly SourceFileSnapshot[],
): SourceComparison {
  if (sameSourceManifest(recorded, current)) return { changed: false, message: '' }
  return {
    changed: true,
    message: '來源的檔名、大小或修改時間與這次上傳建立時不同。'
      + '若只是搬動或還原過檔案，可以「仍以既有 session 續傳」，已上傳的分片不會重傳；'
      + '若內容真的換過，請選「放棄並重新上傳」。',
  }
}

export function createPublicationSnapshot(
  comic: Comic,
  state: ReaderState,
  // 必須包一層呼叫，不能直接寫 crypto.randomUUID：那樣會把原生方法從 crypto 上摘下來，
  // 呼叫時 this 是 undefined，瀏覽器丟 TypeError: Illegal invocation。而它只在作品有
  // 閱讀進度或書籤時才被呼叫，因此沒讀過的作品照常上傳，讀過的每一本都失敗。
  createMutationId: () => string = () => crypto.randomUUID(),
): PublicationSnapshot {
  if (!comic.cloudComicId) throw new Error(`作品「${comic.title}」還沒有雲端目錄識別碼，請先完成註冊再上傳。`)
  return {
    comicId: comic.cloudComicId,
    title: comic.title,
    author: comic.author,
    coverPage: state.coverPage,
    progress: state.progress ? { ...state.progress, mutationId: createMutationId() } : null,
    bookmarks: state.bookmarks.map(bookmark => ({ ...bookmark, mutationId: createMutationId() })),
  }
}
